/**
 * Main Application Entry Point.
 * Clean Code: High-level orchestrator initializing components.
 */

import { State, pushHistory, undoState, redoState } from './state.js';
import { STYLES } from './core/data.js';
import { setupTabNavigation } from './ui/tabs.js';
import { setupYeastTab, populateYeastSelector } from './ui/yeast.js';
import { setupMashTab } from './ui/mash.js';
import { setupWaterTab, populateWaterProfileSelector } from './ui/water.js';
import { setupEquipmentListeners, syncEquipmentFromUI, populateEquipmentSelector } from './ui/equipment.js';
import {
  openFermentableModal,
  openHopModal,
  openModal,
  closeModal,
  setupModalClose,
  filterModalList,
} from './ui/modals.js';
import {
  saveRecipe,
  newRecipe,
  openRecipeModal,
  openPresetRecipesModal,
  scaleCurrentRecipe,
  exportJSON,
  exportFullWorkspaceBackup,
  exportBeerXMLFile,
  importJSON,
  loadDefaultRecipe,
  checkAndRestoreSession,
  syncUIFromState,
  getSavedRecipes,
} from './ui/recipes.js';
import { recalculate, setupMobileSidebar } from './ui/sidebar.js';
import { initMobileApp } from './ui/mobile/mobileApp.js';
import { toggleBrewdayView } from './ui/brewday.js';
import { setupPrintHandler, triggerPrint } from './ui/print.js';
import { debounce } from './ui/utils.js';
import { setupInputSteppers } from './ui/stepper.js';
import { setupToolsModal } from './ui/tools.js';
import { setupBatchesView } from './ui/batches.js';
import { showToast } from './ui/toast.js';
import { safeStorage, onQuotaExceeded } from './core/storage.js';
import { initErrorBoundary } from './ui/errorBoundary.js';
import { mountModals } from './ui/templates/modalsTemplate.js';
import { mountMobileAppView } from './ui/mobile/mobileTemplate.js';
import { setupShareQrModal, handleIncomingShareHash } from './ui/shareQr.js';
import { setupDashboard, switchDesktopView } from './ui/dashboard.js';

// Initialize global crash recovery & error boundary
initErrorBoundary();

const debouncedRecalculate = debounce(recalculate, 150);

onQuotaExceeded(() => {
  showToast(
    '⚠️ Webbläsarens lagringsutrymme är fullt! Nya ändringar hålls temporärt i minnet. Exportera recept som JSON för att inte förlora data.',
    'error',
    10000
  );
});

document.addEventListener('DOMContentLoaded', () => {
  mountModals();
  mountMobileAppView();

  populateEquipmentSelector();
  populateStyleSelector();
  populateWaterProfileSelector();
  populateYeastSelector();

  setupTabNavigation();
  setupHeaderControls();
  setupKeyboardShortcuts();
  setupRecipeInputs();
  setupEquipmentListeners(debouncedRecalculate);

  setupFermentableEvents();
  setupHopEvents();
  setupYeastTab(debouncedRecalculate);
  setupMashTab(debouncedRecalculate);
  setupWaterTab(debouncedRecalculate);
  setupModalClose();
  setupMobileSidebar();
  initMobileApp(recalculate);

  setupPrintHandler();
  setupToolsModal(debouncedRecalculate);
  setupBatchesView(recalculate);
  setupShareQrModal();
  setupDashboard(recalculate);

  const importedFromHash = handleIncomingShareHash(recalculate);
  let restored = false;
  if (!importedFromHash) {
    restored = checkAndRestoreSession(recalculate);
    if (!restored) {
      loadDefaultRecipe(recalculate);
    }
  } else {
    switchDesktopView('recipe');
  }

  setupInputSteppers(debouncedRecalculate);
  recalculate();
  showIbuModelNoticeOnce(restored);
  registerServiceWorker();
});

/**
 * One-time notice: IBU values changed with the improved model (boil gravity
 * + temperature-dependent whirlpool). Only shown to returning users.
 * @param {boolean} restored - Whether a previous session was restored
 */
function showIbuModelNoticeOnce(restored) {
  const KEY = 'brygglabbet_ibu_model_v2_notice';
  try {
    if (safeStorage.getItem(KEY)) return;
    safeStorage.setItem(KEY, '1');
    if (!restored && Object.keys(getSavedRecipes()).length === 0) return;
    showToast(
      'Ny IBU-beräkning: tar nu hänsyn till kokgravitet och whirlpooltemperatur. IBU i sparade recept kan visas 10–20 % lägre än tidigare.',
      'info',
      9000
    );
  } catch {
    // storage unavailable – skip notice
  }
}

function populateStyleSelector() {
  const selIds = ['recipe-style', 'mobile-recipe-style'];
  const categories = [...new Set(STYLES.map((s) => s.category))];

  selIds.forEach((id) => {
    const sel = document.getElementById(id);
    if (!sel) return;

    sel.innerHTML = id.startsWith('mobile-')
      ? '<option value="">— Välj ölstil —</option>'
      : '<option value="">— Välj stil —</option>';

    categories.forEach((cat) => {
      const group = document.createElement('optgroup');
      group.label = cat;
      STYLES.filter((s) => s.category === cat).forEach((s) => {
        const opt = document.createElement('option');
        opt.value = s.id;
        opt.textContent = `${s.id} – ${s.name}`;
        group.appendChild(opt);
      });
      sel.appendChild(group);
    });
  });
}

function setupHeaderControls() {
  document.getElementById('recipe-name-input')?.addEventListener('focus', () => pushHistory());
  document.getElementById('recipe-name-input')?.addEventListener('input', (e) => {
    State.recipe.name = e.target.value;
  });
  document.getElementById('btn-new')?.addEventListener('click', () => {
    pushHistory();
    newRecipe(recalculate);
    switchDesktopView('recipe');
  });
  document.getElementById('btn-open')?.addEventListener('click', () => {
    openRecipeModal(() => {
      recalculate();
      switchDesktopView('recipe');
    });
  });
  document.getElementById('btn-presets')?.addEventListener('click', () => {
    openPresetRecipesModal(() => {
      recalculate();
      switchDesktopView('recipe');
    });
  });
  document.getElementById('btn-save')?.addEventListener('click', saveRecipe);
  document.getElementById('btn-toggle-brewday')?.addEventListener('click', () => toggleBrewdayView(recalculate));
  document.getElementById('btn-swe-info')?.addEventListener('click', () => openModal('modal-swe-info'));
  document.getElementById('btn-print')?.addEventListener('click', triggerPrint);

  // Kebab / Dropdown menu toggle
  const menuBtn = document.getElementById('btn-header-menu');
  const dropdown = document.getElementById('header-dropdown-menu');
  if (menuBtn && dropdown) {
    menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown.classList.toggle('open');
    });

    document.addEventListener('click', () => dropdown.classList.remove('open'));

    dropdown.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', () => dropdown.classList.remove('open'));
    });
  }

  // Undo & Redo
  document.getElementById('btn-undo')?.addEventListener('click', () => {
    undoState(() => syncUIFromState(recalculate));
  });
  document.getElementById('btn-redo')?.addEventListener('click', () => {
    redoState(() => syncUIFromState(recalculate));
  });

  // Export & Import in Header
  document.getElementById('btn-export')?.addEventListener('click', exportJSON);
  document.getElementById('btn-export-beerxml')?.addEventListener('click', exportBeerXMLFile);
  document.getElementById('btn-export-full-backup')?.addEventListener('click', exportFullWorkspaceBackup);
  document.getElementById('btn-import')?.addEventListener('click', () => {
    document.getElementById('import-file-input')?.click();
  });
  document.getElementById('import-file-input')?.addEventListener('change', (e) => {
    pushHistory();
    importJSON(e, () => {
      recalculate();
      switchDesktopView('recipe');
    });
  });

  // Recipe action triggers inside Section 7 (Anteckningar & CTA)
  document.getElementById('cta-btn-start-brewday')?.addEventListener('click', () => {
    toggleBrewdayView(recalculate);
  });
  document.getElementById('cta-btn-print')?.addEventListener('click', triggerPrint);

  document
    .getElementById('tab-btn-preset-recipes')
    ?.addEventListener('click', () => {
      pushHistory();
      openPresetRecipesModal(recalculate);
    });
  document
    .getElementById('tab-btn-scale-recipe')
    ?.addEventListener('click', () => openScaleModal());

  document.getElementById('btn-confirm-scale')?.addEventListener('click', () => {
    pushHistory();
    const targetVol = document.getElementById('scale-target-volume')?.value;
    const targetEff = document.getElementById('scale-target-efficiency')?.value;
    scaleCurrentRecipe(targetVol, targetEff, recalculate);
    closeModal('modal-scale-recipe');
  });
}

function setupKeyboardShortcuts() {
  document.addEventListener('keydown', (e) => {
    const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      if (!isInput || (isInput && document.activeElement?.type === 'checkbox')) {
        if (e.shiftKey) {
          e.preventDefault();
          redoState(() => syncUIFromState(recalculate));
        } else {
          e.preventDefault();
          undoState(() => syncUIFromState(recalculate));
        }
      }
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
      if (!isInput) {
        e.preventDefault();
        redoState(() => syncUIFromState(recalculate));
      }
    }
  });
}

function openScaleModal() {
  const volInput = document.getElementById('scale-target-volume');
  const effInput = document.getElementById('scale-target-efficiency');
  if (volInput) volInput.value = State.recipe.batchVolume || 20;
  if (effInput) effInput.value = State.recipe.efficiency || 75;
  openModal('modal-scale-recipe');
}

function setupRecipeInputs() {
  const ids = ['batch-volume', 'boil-volume', 'boil-time', 'efficiency', 'att-min', 'att-max'];
  ids.forEach((id) => {
    document.getElementById(id)?.addEventListener('input', () => {
      syncRecipeFromUI();
      debouncedRecalculate();
    });
  });

  document.getElementById('recipe-notes')?.addEventListener('input', (e) => {
    State.recipe.notes = e.target.value;
    debouncedRecalculate();
  });
  document.getElementById('recipe-style')?.addEventListener('change', (e) => {
    State.recipe.styleId = e.target.value;
    recalculate();
  });
}

function syncRecipeFromUI() {
  State.recipe.batchVolume = parseFloat(document.getElementById('batch-volume')?.value) || 20;
  State.recipe.boilVolume = parseFloat(document.getElementById('boil-volume')?.value) || 25;
  State.recipe.boilTime = parseFloat(document.getElementById('boil-time')?.value) || 60;
  State.recipe.efficiency = parseFloat(document.getElementById('efficiency')?.value) || 75;
  State.yeast.attMin = parseFloat(document.getElementById('att-min')?.value) || 72;
  State.yeast.attMax = parseFloat(document.getElementById('att-max')?.value) || 78;

  // Sync back to State.equipment as well
  syncEquipmentFromUI();
}

function setupFermentableEvents() {
  const openFerm = () => openFermentableModal(recalculate);
  document.getElementById('btn-add-fermentable')?.addEventListener('click', openFerm);
  document.getElementById('btn-empty-add-fermentable')?.addEventListener('click', openFerm);
  document.getElementById('fermentable-search')?.addEventListener('input', (e) => {
    filterModalList('fermentable-list', e.target.value);
  });
}

function setupHopEvents() {
  const openHop = () => openHopModal(recalculate);
  document.getElementById('btn-add-hop')?.addEventListener('click', openHop);
  document.getElementById('btn-empty-add-hop')?.addEventListener('click', openHop);
  document.getElementById('hop-search')?.addEventListener('input', (e) => {
    filterModalList('hop-list', e.target.value);
  });
}

/**
 * Registers the offline PWA Service Worker in production or HTTPS environments.
 */
export function registerServiceWorker() {
  if (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    (import.meta.env?.PROD || window.location.protocol === 'https:')
  ) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('./sw.js')
        .then((reg) => {
          reg.onupdatefound = () => {
            const installing = reg.installing;
            if (installing) {
              installing.onstatechange = () => {
                if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                  console.warn('[Brygglabbet SW] Ny version installerad och redo.');
                }
              };
            }
          };
        })
        .catch((err) => {
          console.warn('[Brygglabbet SW] Registrering misslyckades:', err);
        });
    });
  }
}


